<template>
    <div>
        <p v-if="errorMessage" style="color: red; margin-bottom: 8px;">
            {{ errorMessage }}
        </p>
        <div id="stripe-payment-element" />
    </div>
</template>

<script>
import { buildStripeAppearance } from '@/utils/stripeAppearance.util';
import { loadStripe } from '@stripe/stripe-js';

export default {
    props: {
        clientSecret: {
            type: String,
            required: true,
        },
        sessionKey: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            stripe: null,
            elements: null,
            errorMessage: null,
            isReady: false,
            isProcessing: false,
            initializationFailed: false,
        };
    },

    async mounted() {
        // Register the listener before async init and mark the container ready so callers can detect actual mounting, not just wrapper presence.
        if (this.$el.parentElement) {
            this.$el.parentElement.addEventListener('stripe-submit', this.handleSubmit);
            this.$el.parentElement.dataset.stripeSubmitReady = 'true';
        }

        try {
            this.stripe = await loadStripe(this.sessionKey);

            if (!this.stripe) {
                throw new Error('Stripe failed to initialize.');
            }

            this.elements = this.stripe.elements({ clientSecret: this.clientSecret, appearance: buildStripeAppearance() });
            const paymentElement = this.elements.create('payment', { layout: { type: 'tabs' }, wallets: { link: 'never' } });

            paymentElement.on('ready', () => {
                this.isReady = true;
            });

            paymentElement.mount('#stripe-payment-element');
        } catch (error) {
            // Initialization failed permanently (as opposed to still loading); mark the container
            // not-ready so callers stop dispatching stripe-submit to a component that can never confirm payment.
            this.initializationFailed = true;
            this.errorMessage = 'Unable to load the payment form. Please refresh the page and try again.';

            if (this.$el.parentElement) {
                delete this.$el.parentElement.dataset.stripeSubmitReady;
            }
        }
    },

    beforeDestroy() {
        if (this.$el.parentElement) {
            this.$el.parentElement.removeEventListener('stripe-submit', this.handleSubmit);
            delete this.$el.parentElement.dataset.stripeSubmitReady;
        }
    },

    methods: {
        async handleSubmit() {
            if (this.initializationFailed) {
                return;
            }

            if (!this.elements || !this.isReady) {
                this.errorMessage = 'Payment form is still loading. Please wait a moment and try again.';

                return;
            }

            if (this.isProcessing) {
                return;
            }

            this.isProcessing = true;
            this.errorMessage = null;

            try {
                const setupIntent = await this.confirmStripeSetup();

                // Detect form ID - Shopping Cart uses #oneStepCheckout, Order Forms use #checkout
                const formId = jQuery('#oneStepCheckout').length > 0 ? '#oneStepCheckout' : '#checkout';

                const isMultiStep = jQuery('#selectPayment').length > 0;

                if (isMultiStep) {
                    jQuery('#paymentMethodConfigId').val(setupIntent.id);
                    jQuery('#paymentMethodId').val(setupIntent.payment_method);
                    jQuery('#selectPayment').submit();
                } else {
                    jQuery('#stripeSetupIntentId').val(setupIntent.id);
                    jQuery('#stripePaymentMethodToken').val(setupIntent.payment_method);
                    jQuery('#proceedToCheckout').val('true');

                    // Set flag to indicate form submission is from Stripe (bypass validation loop)
                    jQuery('#stripeSubmissionComplete').val('true');

                    jQuery(formId).submit();
                }
            } catch (error) {
                this.errorMessage = error?.message
                        || 'An error occurred while confirming your payment method. Please try again.';
            } finally {
                // CRITICAL: Always reset isProcessing, even if there's an error
                this.isProcessing = false;
            }
        },

        async confirmStripeSetup() {
            const { error: submitError } = await this.elements.submit();

            if (submitError) {
                throw new Error(submitError.message);
            }

            const { setupIntent, error: confirmError } = await this.stripe.confirmSetup({
                elements: this.elements,
                redirect: 'if_required',
                confirmParams: { return_url: window.location.href },
            });

            if (confirmError) {
                throw new Error(confirmError.message);
            }

            return setupIntent;
        },
    }
}
</script>
